/**
 * Apartment CRM — API surface, wired to the Django backend at
 * `/api/v1/oh_apartments/*`. Covers the full CRM: org (clusters / complexes /
 * operators), teachers, scheduling, attendance, payments, inventory, incidents,
 * trials/waitlist, dashboards and reports. Most per-building reads take an
 * `apartment` query param = the building slug (the id the FE selector uses).
 *
 * No mock data: every screen reads the backend. `./apartmentData.js` is kept only
 * for the static building list used as a fallback if `/complexes/` cannot be read.
 */
import { apiEndpoints } from 'api/common'
import { get, patch, post, remove } from 'request'
import { sentryLogger } from 'sentry'

import { APARTMENTS } from './apartmentData'

export { APARTMENTS }
export const DEFAULT_APARTMENT_ID = APARTMENTS[0].id

// Lazy getter — `apiEndpoints` lives in api/common, which sits in a circular
// import with the api barrel. Reading it at module-eval time would hit its
// temporal dead zone; defer the access to call time like the other api files.
// REACT_APP_APARTMENTS_API_BASE optionally points the apartment calls at a
// different origin (e.g. a local backend during the frontend-first build);
// empty/unset falls back to the normal API origin.
const base = () =>
  process.env.REACT_APP_APARTMENTS_API_BASE || apiEndpoints.apartments
// List endpoints are DRF-paginated (page size 25); ask for everything so the
// client-side tables can paginate the full set themselves.
const LIST_LIMIT = 10000

// Unwraps DRF's { count, next, previous, results } envelope to a bare array.
const listResults = (response) => response?.data?.results || []

// List of buildings (complexes) for the apartment selector. Throws on failure
// so the caller (apartmentStore) keeps the static fallback and retries later —
// e.g. if the first call fires before auth on a deployed environment.
export const fetchApartments = async () => {
  const res = await get(`${base()}/complexes/`)
  const rows = Array.isArray(res.data) ? res.data : res.data?.results || []
  return rows.map((a) => ({ id: a.slug, name: a.name }))
}

// --- Org admin: clusters / complexes / operators ---------------------------

const listData = (res) =>
  Array.isArray(res.data) ? res.data : res.data?.results || []

export const fetchClusters = async () => {
  try {
    return listData(await get(`${base()}/clusters/`))
  } catch (error) {
    sentryLogger(error, 'fetchClusters')
    throw error
  }
}
export const createCluster = (payload) =>
  post(`${base()}/clusters/`, payload).then((r) => r.data)
export const updateCluster = (uuid, payload) =>
  patch(`${base()}/clusters/${uuid}/`, payload).then((r) => r.data)
export const deleteCluster = (uuid) => remove(`${base()}/clusters/${uuid}/`)

export const fetchComplexes = async () => {
  try {
    return listData(await get(`${base()}/complexes/`))
  } catch (error) {
    sentryLogger(error, 'fetchComplexes')
    throw error
  }
}
export const createComplex = (payload) =>
  post(`${base()}/complexes/`, payload).then((r) => r.data)
export const updateComplex = (slug, payload) =>
  patch(`${base()}/complexes/${slug}/`, payload).then((r) => r.data)

// Operator (ApartmentOperator) CRUD used to live here, behind At Apartment ->
// Operators. That tab was removed (2026-08-31) in favour of HQ -> Roles &
// Permissions. The /operators/ endpoints and the existing rows are untouched —
// they still drive procurement approvals and apartment scoping — but nothing in
// Alfred writes them any more; use Django admin.

// --- Families (parents) + children (students) -------------------------------
// Apartment people ARE main-system users now. There is no apartment-side person
// CRUD any more — create and edit them under HQ → Users. These read-only
// helpers hit the main endpoints with the `?apartment=<slug>` filter and
// flatten the nested `user` object down to the shape the pickers expect.

const personName = (user) =>
  `${user?.first_name || ''} ${user?.last_name || ''}`.trim()

// The main list endpoints are DRF-paginated and nest the person under `user`.
const mainList = async (url, context) => {
  try {
    const res = await get(url)
    return res?.data?.results || (Array.isArray(res?.data) ? res.data : [])
  } catch (error) {
    sentryLogger(error, context)
    throw error
  }
}

export const fetchFamilies = async (apartmentId) => {
  const rows = await mainList(
    `${apiEndpoints.parent}?apartment=${apartmentId}&limit=${LIST_LIMIT}`,
    'fetchFamilies'
  )
  return rows.map((p) => ({
    uuid: p.user_id,
    parent_name: personName(p.user),
    primary_phone: p.user?.phone_number || ''
  }))
}

export const fetchChildren = async (apartmentId) => {
  const rows = await mainList(
    `${apiEndpoints.student}?apartment=${apartmentId}&limit=${LIST_LIMIT}`,
    'fetchChildren'
  )
  return rows.map((s) => ({ uuid: s.user_id, name: personName(s.user) }))
}

// --- Searchable user pickers ------------------------------------------------
// Every user picker in this module searches by name OR phone number, matching
// the main system's parent search. `name` is what AutoComplete renders.

const NUMERIC = /^\d+$/
const searchQuery = (text) =>
  NUMERIC.test((text || '').trim())
    ? `phone_number=${encodeURIComponent(text.trim())}`
    : `name=${encodeURIComponent((text || '').trim())}`

const withPhone = (name, phone) => (phone ? `${name} · ${phone}` : name)

// Parents in one building, or — when `apartmentId` is falsy — across every
// parent in Alfred. Returns AutoComplete options carrying the ids the booking +
// dashboard flows need.
//
// The scope MUST be dropped by omitting the param, never by sending it empty or
// undefined. `apartment` is an optional CharFilter on the backend's ParentFilter
// (apps/oh_users/student/api/filters/parent_filter.py), so django-filter skips
// it only when it is absent; `?apartment=undefined` is a real value that matches
// no building and quietly returns nobody.
export const searchApartmentParents = async (apartmentId, text = '') => {
  const scope = apartmentId ? `apartment=${apartmentId}&` : ''
  const rows = await mainList(
    `${apiEndpoints.parent}?${scope}${searchQuery(text)}&limit=50`,
    'searchApartmentParents'
  )
  return rows.map((p) => ({
    uuid: p.user_id,
    value: p.user_id,
    name: withPhone(personName(p.user), p.user?.phone_number),
    phone: p.user?.phone_number || '',
    // Children come from the parent, never from a free-floating student list.
    children: (p.students || []).map((s) => ({
      uuid: s.user_id || s.user?.uuid,
      name: personName(s.user)
    }))
  }))
}

// Courses a batch at THIS building may be created against — scoped server-side
// to the programmes the building runs, which is the same list the Pricing tab
// shows.
//
// This used to hit the main system's course endpoint with no apartment filter
// and `limit=50`: 2,294 courses sliced in database order, so an apartment
// running Art & Design and Robotics for nur-class 9 was offered class 10-12
// Physics, Accounts and Hindi tuition. 1,071 of those courses carry no `name`,
// and the label was built from the name alone, which is where the literal
// "null (class 11)" rows came from. Scoped it is 58-63 courses per building and
// none of them are unnamed.
export const fetchCourseOptions = async (apartmentId, text = '') => {
  const q = (text || '').trim()
  const search = q ? `&search=${encodeURIComponent(q)}` : ''
  try {
    const { data } = await get(
      `${base()}/course-options/?apartment=${apartmentId}${search}`
    )
    return (data?.courses || []).map((c) => ({
      uuid: c.uuid,
      value: c.uuid,
      name: c.name
    }))
  } catch (error) {
    sentryLogger(error, 'error in fetching apartment course options')
    return []
  }
}

// Attach a parent to a building (and record their flat). Narrow by design: the
// endpoint writes only these two fields. The main system's Alfred parent PUT
// takes a whole parent payload and calls `students.set(...)` with it, which
// detaches every child the payload omits — 595 children on prod. Never reuse it
// to set a building.
export const setParentBuilding = async (parentUuid, { apartment, flat }) => {
  const { data } = await patch(`${base()}/parents/${parentUuid}/building/`, {
    apartment,
    flat_number: flat
  })
  return data
}

// Admins, searchable, for follow-up owner. AdminUserFilter has no `name`
// filter — it exposes first_name/last_name — so use DRF's SearchFilter, whose
// search_fields already cover first name, last name and phone number.
export const searchAdmins = async (text = '') => {
  const q = (text || '').trim()
  const rows = await mainList(
    `${apiEndpoints.adminUser}?${
      q ? `search=${encodeURIComponent(q)}&` : ''
    }limit=50`,
    'searchAdmins'
  )
  return rows.map((a) => ({
    uuid: a.user?.uuid,
    value: a.user?.uuid,
    name: withPhone(personName(a.user), a.user?.phone_number)
  }))
}

// --- Batches ----------------------------------------------------------------

export const fetchBatches = async (apartmentId) => {
  try {
    return listData(await get(`${base()}/batches/?apartment=${apartmentId}`))
  } catch (error) {
    sentryLogger(error, 'fetchBatches')
    throw error
  }
}
export const createBatch = (payload) =>
  post(`${base()}/batches/`, payload).then((r) => r.data)
export const updateBatch = (uuid, payload) =>
  patch(`${base()}/batches/${uuid}/`, payload).then((r) => r.data)
export const deleteBatch = (uuid) => remove(`${base()}/batches/${uuid}/`)

// --- Scheduling (sessions) --------------------------------------------------

// `date` (YYYY-MM-DD) narrows to one day's sessions — the attendance page shows
// a card per batch occurrence on the chosen date.
export const fetchSessions = async (apartmentId, date) => {
  try {
    const on = date ? `&date=${date}` : ''
    return listData(
      await get(`${base()}/sessions/?apartment=${apartmentId}${on}`)
    )
  } catch (error) {
    sentryLogger(error, 'fetchSessions')
    throw error
  }
}
export const createSession = (payload) =>
  post(`${base()}/sessions/`, payload).then((r) => r.data)
export const updateSession = (uuid, payload) =>
  patch(`${base()}/sessions/${uuid}/`, payload).then((r) => r.data)
export const deleteSession = (uuid) => remove(`${base()}/sessions/${uuid}/`)

export const fetchSessionAttendance = async (sessionUuid) => {
  try {
    return (await get(`${base()}/sessions/${sessionUuid}/attendance/`)).data
  } catch (error) {
    sentryLogger(error, 'fetchSessionAttendance')
    throw error
  }
}
export const markSessionAttendance = (sessionUuid, entries) =>
  post(`${base()}/sessions/${sessionUuid}/attendance/`, { entries }).then(
    (r) => r.data
  )

// Mark the teacher for one session — the apartment twin of the centre page's
// teacher row. Each control sends only its own field: `{ attended }`,
// `{ actual_teacher }` or `{ arrival }`. An omitted field is left alone, an
// explicit null clears it. Returns the fresh teacherAttendance block.
export const markSessionTeacherAttendance = (sessionUuid, payload) =>
  post(`${base()}/sessions/${sessionUuid}/teacher-attendance/`, payload).then(
    (r) => r.data
  )

// Cancel a whole session — off for everyone, and the teacher is told.
export const cancelSession = (sessionUuid, reason = '') =>
  post(`${base()}/sessions/${sessionUuid}/cancel/`, { reason }).then(
    (r) => r.data
  )

// Flip one child's mark. Mirrors the main system's immediate PATCH-per-row:
// no Save button, the server owns the toggle. Returns the session's counts.
export const toggleAttendance = (sessionUuid, child) =>
  post(`${base()}/sessions/${sessionUuid}/attendance/toggle/`, { child }).then(
    (r) => r.data
  )

// Cancel one child's seat at one session — they are excused from it, so it does
// not count against their attendance.
export const cancelSeat = (sessionUuid, child, reason = '') =>
  post(`${base()}/sessions/${sessionUuid}/attendance/cancel/`, {
    child,
    reason
  }).then((r) => r.data)

export const restoreSeat = (sessionUuid, child) =>
  remove(
    `${base()}/sessions/${sessionUuid}/attendance/cancel/?child=${child}`
  ).then((r) => r.data)

// Inventory + purchase requests were removed from the apartment UI in
// 2026-07 ("will be added separately"). The backend endpoints and models
// still exist; the coordinator/cluster dashboards no longer surface them.

// --- Dashboards (coordinator + cluster head) --------------------------------

export const fetchCoordinatorDashboard = async (apartmentId) => {
  try {
    return (
      await get(`${base()}/coordinator-dashboard/?apartment=${apartmentId}`)
    ).data
  } catch (error) {
    sentryLogger(error, 'fetchCoordinatorDashboard')
    throw error
  }
}

export const fetchClusterDashboard = async (clusterSlug = '', month = '') => {
  try {
    const parts = []
    if (clusterSlug) parts.push(`cluster=${clusterSlug}`)
    if (month) parts.push(`month=${month}`)
    const q = parts.length ? `?${parts.join('&')}` : ''
    return (await get(`${base()}/cluster-dashboard/${q}`)).data
  } catch (error) {
    sentryLogger(error, 'fetchClusterDashboard')
    throw error
  }
}

// Parent/section Overview — org-wide rollup, optionally for one apartment.
export const fetchApartmentOverview = async (
  apartmentSlug = '',
  month = '',
  cluster = ''
) => {
  try {
    const parts = []
    if (apartmentSlug) parts.push(`apartment=${apartmentSlug}`)
    if (month) parts.push(`month=${month}`)
    if (cluster) parts.push(`cluster=${cluster}`)
    const q = parts.length ? `?${parts.join('&')}` : ''
    return (await get(`${base()}/overview-dashboard/${q}`)).data
  } catch (error) {
    sentryLogger(error, 'fetchApartmentOverview')
    throw error
  }
}

// Growth — re-enrolment, new enrolment, win-back, enrolled parents, enrolments
// and revenue for ONE month, per apartment and rolled up per cluster.
// `month` is 'YYYY-MM'; omitted means the current month.
export const fetchGrowthDashboard = async (month = '', cluster = '') => {
  try {
    const parts = []
    if (month) parts.push(`month=${month}`)
    if (cluster) parts.push(`cluster=${cluster}`)
    const q = parts.length ? `?${parts.join('&')}` : ''
    return (await get(`${base()}/growth-dashboard/${q}`)).data
  } catch (error) {
    sentryLogger(error, 'fetchGrowthDashboard')
    throw error
  }
}

// The months that actually HAVE data, newest first, from the backend —
// [{value: '2026-07', label: "Jul '26"}]. Optionally scoped to one apartment.
//
// This replaces a rolling 12-month window computed in the browser, which
// offered eleven months with nothing behind them; every dashboard then answered
// those months with zeros and looked broken.
export const fetchBillingMonths = async (apartmentId = '') => {
  try {
    const q = apartmentId ? `?apartment=${apartmentId}` : ''
    const res = await get(`${base()}/billing-months/${q}`)
    return Array.isArray(res.data) ? res.data : []
  } catch (error) {
    sentryLogger(error, 'fetchBillingMonths')
    return []
  }
}

// A single family's 360° view — parent, children + attendance, enrolments,
// payment history (pick a family by uuid).
// The batches a child may trial in, each carrying the dates it actually runs
// over the next month. Without a child, every running batch at the building.
export const fetchTrialOptions = async (apartmentId, childId) => {
  try {
    const child = childId ? `&child=${childId}` : ''
    const res = await get(
      `${base()}/trial-options/?apartment=${apartmentId}${child}`
    )
    return res.data?.batches || []
  } catch (error) {
    sentryLogger(error, 'fetchTrialOptions')
    return []
  }
}

// One child's apartment enrolments + attendance — the drawer the Family card
// opens. The apartment twin of StudentHistoryDrawer's two main-system calls.
export const fetchChildHistory = async (childUuid) => {
  try {
    return (await get(`${base()}/children/${childUuid}/history/`)).data
  } catch (error) {
    sentryLogger(error, 'fetchChildHistory')
    throw error
  }
}

export const fetchFamilyDashboard = async (familyUuid, month = '') => {
  try {
    const m = month ? `&month=${month}` : ''
    return (await get(`${base()}/family-dashboard/?family=${familyUuid}${m}`))
      .data
  } catch (error) {
    sentryLogger(error, 'fetchFamilyDashboard')
    throw error
  }
}

// --- Trials + waitlist ------------------------------------------------------

export const fetchTrials = async (apartmentId) => {
  try {
    return listData(await get(`${base()}/trials/?apartment=${apartmentId}`))
  } catch (error) {
    sentryLogger(error, 'fetchTrials')
    throw error
  }
}
export const createTrial = (payload) =>
  post(`${base()}/trials/`, payload).then((r) => r.data)
export const updateTrial = (uuid, payload) =>
  patch(`${base()}/trials/${uuid}/`, payload).then((r) => r.data)

export const fetchWaitlist = async (apartmentId) => {
  try {
    return listData(await get(`${base()}/waitlist/?apartment=${apartmentId}`))
  } catch (error) {
    sentryLogger(error, 'fetchWaitlist')
    throw error
  }
}
export const createWaitlistEntry = (payload) =>
  post(`${base()}/waitlist/`, payload).then((r) => r.data)
export const updateWaitlistEntry = (uuid, payload) =>
  patch(`${base()}/waitlist/${uuid}/`, payload).then((r) => r.data)
export const deleteWaitlistEntry = (uuid) =>
  remove(`${base()}/waitlist/${uuid}/`)

// --- Issues & Notes ---------------------------------------------------------
// Shared by centres and apartments since 2026-07. Scope with exactly one of
// { apartment: <slug> } or { centre: <uuid> }.

export const fetchCentres = async () => {
  try {
    return listData(await get(`${apiEndpoints.centre}?limit=${LIST_LIMIT}`))
  } catch (error) {
    sentryLogger(error, 'fetchCentres')
    throw error
  }
}

export const fetchIncidents = async ({ apartment, centre } = {}) => {
  const scope = apartment
    ? `?apartment=${apartment}`
    : centre
    ? `?centre=${centre}`
    : ''
  try {
    return listData(await get(`${base()}/incidents/${scope}`))
  } catch (error) {
    sentryLogger(error, 'fetchIncidents')
    throw error
  }
}
export const createIncident = (payload) =>
  post(`${base()}/incidents/`, payload).then((r) => r.data)
export const updateIncident = (uuid, payload) =>
  patch(`${base()}/incidents/${uuid}/`, payload).then((r) => r.data)
export const deleteIncident = (uuid) => remove(`${base()}/incidents/${uuid}/`)
export const resolveIncident = (uuid, payload) =>
  post(`${base()}/incidents/${uuid}/resolve/`, payload).then((r) => r.data)

// --- Teachers ---------------------------------------------------------------
// Read-only, from the main Users API. `basic=1` returns the light serializer.
// Teachers are onboarded and mapped to a centre or an apartment under HQ → Users.

export const fetchTeachers = async (apartmentId) => {
  const scope = apartmentId ? `&apartment=${apartmentId}` : ''
  const rows = await mainList(
    `${apiEndpoints.teacher}?basic=1${scope}&limit=${LIST_LIMIT}`,
    'fetchTeachers'
  )
  // BasicTeacherSerializer exposes `uuid` (sourced from user.uuid), not `user_id`.
  return rows.map((t) => ({ uuid: t.uuid, name: personName(t.user) }))
}

// Teachers a batch can be given, searched server-side by name or phone.
//
// NOT scoped to the building. `fetchTeachers(apartmentId)` filters on
// Teacher.complexes, which nothing in Alfred or the API ever writes — on prod
// no teacher is linked to any building, so that dropdown was always empty.
// Excludes only the teachers explicitly marked inactive or terminated: almost
// nobody reads 'active' (every current apartment teacher is 'applied').
// Server-side search keeps each keystroke to one small page instead of ~1,200
// rows.
export const searchBatchTeachers = async (text = '') => {
  const q = encodeURIComponent((text || '').trim())
  const rows = await mainList(
    `${apiEndpoints.teacher}?basic=1&exclude_status=inactive,terminated` +
      `&search=${q}&limit=50`,
    'searchBatchTeachers'
  )
  return rows
    .map((t) => {
      const name = personName(t.user)
      const phone = t.user?.phone_number
      // Phone disambiguates namesakes across ~1,200 teachers.
      return { uuid: t.uuid, name: phone ? `${name} (${phone})` : name }
    })
    .sort((a, b) => a.name.localeCompare(b.name))
}

// --- Per-building fetchers (Slice 1 — real backend) ------------------------

export const fetchApartmentDashboard = async (apartmentId, month = '') => {
  try {
    const m = month ? `&month=${month}` : ''
    const res = await get(`${base()}/dashboard/?apartment=${apartmentId}${m}`)
    return res.data
  } catch (error) {
    sentryLogger(error, 'fetchApartmentDashboard')
    throw error
  }
}

export const fetchApartmentEnrolments = async (apartmentId) => {
  try {
    const res = await get(
      `${base()}/enrolments/?apartment=${apartmentId}&limit=${LIST_LIMIT}`
    )
    return listResults(res)
  } catch (error) {
    sentryLogger(error, 'fetchApartmentEnrolments')
    throw error
  }
}

// `month` is 'YYYY-MM'. The summary cards are derived from these rows, so
// filtering here is what makes them report the month rather than all time.
export const fetchApartmentPayments = async (apartmentId, month = '') => {
  try {
    const m = month ? `&month=${month}` : ''
    const res = await get(
      `${base()}/payments/?apartment=${apartmentId}&limit=${LIST_LIMIT}${m}`
    )
    return listResults(res)
  } catch (error) {
    sentryLogger(error, 'fetchApartmentPayments')
    throw error
  }
}

export const fetchApartmentPricing = async (apartmentId) => {
  try {
    const res = await get(`${base()}/pricing/?apartment=${apartmentId}`)
    return res.data
  } catch (error) {
    sentryLogger(error, 'fetchApartmentPricing')
    throw error
  }
}

// Derives the New Enrolment form options (students / programs / bookable
// day+time slots per program) from the backend. Each slot carries `batch`
// (the batch uuid) — the payment-link create flow sends it back as the slot id.
export const apartmentEnrolmentOptions = async (apartmentId) => {
  try {
    const res = await get(
      `${base()}/enrolment-options/?apartment=${apartmentId}`
    )
    return res.data
  } catch (error) {
    sentryLogger(error, 'apartmentEnrolmentOptions')
    throw error
  }
}

// Live search over ALL main-system parents by name/phone (not just this
// apartment's families). Each parent comes back with its children shaped like
// the enrolment-options payload (eligibility resolved for this apartment), so a
// searched parent can be booked exactly like a loaded one.
export const apartmentParentSearch = async (apartmentId, query) => {
  const q = (query || '').trim()
  if (q.length < 2) return []
  try {
    const res = await get(
      `${base()}/parents/search/?apartment=${apartmentId}&q=${encodeURIComponent(
        q
      )}`
    )
    return Array.isArray(res.data) ? res.data : res.data?.results || []
  } catch (error) {
    sentryLogger(error, 'apartmentParentSearch')
    return []
  }
}

// Parents and their students (one row per student) for the People screen.
// No dedicated backend endpoint yet (later slice) — derived from the real
// enrolments list, matching the original mock derivation.
export const fetchApartmentPeople = async (apartmentId) => {
  const enr = await fetchApartmentEnrolments(apartmentId)
  const byStudent = {}
  enr.forEach((e) => {
    if (!e.childName) return
    const key = `${e.childName}|${e.parentName}`
    if (!byStudent[key]) {
      byStudent[key] = {
        parentName: e.parentName,
        phone: e.phone || '',
        student: e.childName,
        age: e.age || '',
        programs: new Set()
      }
    }
    if (e.program) byStudent[key].programs.add(e.program)
  })
  return Object.values(byStudent).map((r) => ({
    parentName: r.parentName,
    phone: r.phone,
    student: r.student,
    age: r.age,
    programs: [...r.programs].join(', ')
  }))
}

// --- Payment-link create / resend (Slice 1 — real backend) -----------------
// `selections` = [{ program, slots: [{ batch }] }] where batch is the batch
// uuid from apartmentEnrolmentOptions(...).batchesByProgram[program][].batch.

// New booking: one parent, one or more children, one bill, one Razorpay link.
// `children` = [{ child: <uuid>, selections: [{ program, slots }] }]
export const createApartmentPaymentLink = async ({
  apartmentId,
  parent,
  children,
  startDate,
  billingMonth
}) => {
  try {
    const res = await post(`${base()}/payment-link/`, {
      apartment: apartmentId,
      parent,
      children,
      // The backend derives the end date from the prorata rule.
      ...(startDate ? { start_date: startDate } : {}),
      ...(billingMonth ? { billing_month: billingMonth } : {})
    })
    return res.data
  } catch (error) {
    sentryLogger(error, 'createApartmentPaymentLink')
    throw error
  }
}

export const recordOfflinePayment = (uuid, payload) =>
  post(`${base()}/payment-link/${uuid}/record-offline/`, payload).then(
    (r) => r.data
  )

export const createFamilyPaymentLink = (payload) =>
  post(`${base()}/payment-link/family/`, payload).then((r) => r.data)

export const fetchRevenueMatrix = async (query = '') => {
  try {
    return (await get(`${base()}/reports/revenue-matrix/${query}`)).data
  } catch (error) {
    sentryLogger(error, 'fetchRevenueMatrix')
    throw error
  }
}

// Exports what the page is showing: the month filter is passed through, so the
// CSV matches the rows (and the summary cards) on screen rather than all time.
export const paymentsCsvUrl = (apartmentId, month = '') =>
  `${base()}/reports/payments-csv/?apartment=${apartmentId}${
    month ? `&month=${month}` : ''
  }`

export const misCsvUrl = () => `${base()}/reports/mis-csv/`

export const fetchTeacherUtilisation = async (month = '') => {
  try {
    const q = month ? `?month=${month}` : ''
    return listData(await get(`${base()}/reports/teacher-utilisation/${q}`))
  } catch (error) {
    sentryLogger(error, 'fetchTeacherUtilisation')
    throw error
  }
}

export const resendApartmentPaymentLink = async (uuid) => {
  try {
    const res = await post(`${base()}/payment-link/${uuid}/resend/`, {})
    return res.data
  } catch (error) {
    sentryLogger(error, 'resendApartmentPaymentLink')
    throw error
  }
}

// PATCH the building's pricing. The backend rejects (403) callers not on the
// pricing-editor allowlist. `payload` = { programs, discountTiers?,
// effective_from? }. Returns the rebuilt pricing config.
export const updateApartmentPricing = async (apartmentId, payload) => {
  try {
    const res = await patch(
      `${base()}/pricing/?apartment=${apartmentId}`,
      payload
    )
    return res.data
  } catch (error) {
    sentryLogger(error, 'updateApartmentPricing')
    throw error
  }
}

// --- Payout ----------------------------------------------------------------
// One revenue-share settlement per apartment per month. The amounts are NOT
// stored: the backend recomputes them from that month's paid payment links on
// every read, so a late payment moves the figure even after the month has been
// marked paid. Only `paid` and `notes` are writable.
//
// Rows are created by a monthly task (1st, for the month just ended) and on
// read, so a month with revenue always has a row.

export const fetchApartmentPayout = async (apartmentId, month = '') => {
  try {
    const m = month ? `&month=${month}` : ''
    const res = await get(`${base()}/payout/?apartment=${apartmentId}${m}`)
    return res.data
  } catch (error) {
    sentryLogger(error, 'fetchApartmentPayout')
    throw error
  }
}

// Returns the whole refreshed payload, so the screen re-renders from the
// server's numbers rather than patching its own local copy.
export const updateApartmentPayout = async (uuid, values) => {
  try {
    const res = await patch(`${base()}/payout/${uuid}/`, values)
    return res.data
  } catch (error) {
    sentryLogger(error, 'updateApartmentPayout')
    throw error
  }
}

export const updateApartmentPayoutAccount = async (
  apartmentId,
  accountDetails
) => {
  try {
    const res = await patch(
      `${base()}/payout/account/?apartment=${apartmentId}`,
      {
        accountDetails
      }
    )
    return res.data
  } catch (error) {
    sentryLogger(error, 'updateApartmentPayoutAccount')
    throw error
  }
}

export const apartmentPaymentsSummary = (rows = []) => {
  const collected = rows
    .filter((r) => r.status === 'Paid')
    .reduce((sum, r) => sum + Number(r.amount), 0)
  const pending = rows
    .filter((r) => r.status === 'Pending')
    .reduce((sum, r) => sum + Number(r.amount), 0)
  const expiredCount = rows.filter((r) => r.status === 'Expired').length
  return { collected, pending, expiredCount, total: rows.length }
}

// --- Pricing helpers -------------------------------------------------------
// Family-level discount tiers apply to total enrolment count. The authoritative
// config now comes from fetchApartmentPricing(...); this constant is only a
// fallback for the live cost preview before that resolves.

export const PRICING_CONFIG = {
  gstPercent: 18,
  gstInclusive: true,
  programs: [
    { program: 'Art', onceWeekly: 3000, twiceWeekly: 6000 },
    { program: 'Public Speaking', onceWeekly: 3000, twiceWeekly: 6000 },
    { program: 'Dance', onceWeekly: 2000, twiceWeekly: 4000 },
    { program: 'Robotics', onceWeekly: 4000, twiceWeekly: 8000 }
  ],
  discountTiers: [
    { minEnrolments: 2, discountPercent: 10 },
    { minEnrolments: 3, discountPercent: 15 },
    { minEnrolments: 4, discountPercent: 25 }
  ]
}

// Pricing-editor gating now comes from the backend: GET /pricing/ returns a
// `canEdit` flag computed from the logged-in user's allowlist membership, and
// PATCH /pricing/ enforces the same gate server-side.

// Prorata split for a join date. Days 1-14 bill the whole join month and
// nothing else; from the 15th, half the join month plus all of the next.
// MUST stay identical to `prorata_factors` in
// oh-api/apps/oh_apartments/services/fee_calculator.py — this preview is what
// the coordinator quotes the parent, and the backend is what actually charges.
// How much of the JOIN month a booking pays for, by the week its start date
// falls in: 1-7 full, 8-14 three quarters, 15-21 half, 22-end a quarter.
// Changed 2026-09-01 — it used to be one half-month cut, so the 1st and the
// 14th cost the same. MUST stay identical to `prorata_factors` in
// oh-api/apps/oh_apartments/services/fee_calculator.py.
const WEEK_LAST_DAYS = [
  [7, 1],
  [14, 0.75],
  [21, 0.5]
]
const QUARTER = 0.25
// Whether next month is bought on the same bill. A SEPARATE cut from the week
// factors above, deliberately: joining from the 15th buys next month too, so a
// late joiner is not asked to renew a week after they start.
const FIRST_HALF_LAST_DAY = 14

export const prorataFactors = (startDate) => {
  if (!startDate) return { current: 1, next: 0 }
  const day = new Date(startDate).getDate()
  const bucket = WEEK_LAST_DAYS.find(([lastDay]) => day <= lastDay)
  return {
    current: bucket ? bucket[1] : QUARTER,
    next: day <= FIRST_HALF_LAST_DAY ? 0 : 1
  }
}

const round2 = (n) => Math.round(n * 100) / 100

// The last day a booking is paid up to. Derived from the same prorata rule that
// prices it, so the coordinator only ever picks a start date.
// MUST stay identical to `billing_end_date` in
// oh-api/apps/oh_apartments/services/fee_calculator.py
export const billingEndDate = (startDate) => {
  if (!startDate) return null
  const d = new Date(startDate)
  const { next } = prorataFactors(startDate)
  // Day 0 of month N+1 is the last day of month N.
  const end = new Date(d.getFullYear(), d.getMonth() + (next ? 2 : 1), 0)
  const pad = (n) => String(n).padStart(2, '0')
  return `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())}`
}

// Which kits this booking owes, in rupees. Twin of `_kits_owed` in
// oh_apartments/services/payment_link.py: a child pays for a subject's kit once
// per level, so a kit is owed only when the subject has a kit price AND the
// child has not already paid it at that level. De-duplicated within the booking,
// so a 2x-a-week pair (two batches, same subject and level) is one kit.
//
// `selected` = [{ childId, program, level }], `kitsPaidByChild` = { childId:
// [{ program, level }] } — both come straight off the enrolment-options payload.
export const kitTotalFor = (
  selected = [],
  kitsPaidByChild = {},
  config = PRICING_CONFIG
) => {
  const programs = config?.programs || PRICING_CONFIG.programs
  const seen = new Set()
  return selected.reduce((total, { childId, program, level }) => {
    const key = `${childId}|${program}|${level}`
    if (seen.has(key)) return total
    seen.add(key)
    const row = programs.find((p) => p.program === program)
    const amount = Number(row?.kitCharge || 0)
    if (amount <= 0) return total
    const paid = (kitsPaidByChild[childId] || []).some(
      (k) => k.program === program && Number(k.level) === Number(level)
    )
    return paid ? total : total + amount
  }, 0)
}

// Fee calculator: family-level discount tier applied, GST inclusive (no
// breakup). `selections` = [{ program, frequency }]. `config` defaults to the
// fallback but callers should pass the building's fetched pricing.
// `startDate` prorates a first enrolment; omit it for a recurring monthly bill.
// Twin of compute_enrolment_fee in oh_apartments/services/fee_calculator.py.
// `kitTotal` is the one-off kit charges this booking owes. It is never prorated
// and never discounted — a kit costs what it costs — so the preview quotes the
// same number the backend will charge.
// `heldSelections` = [{ program, frequency }] for seats the family ALREADY holds
// (every child's live enrolments). Not charged again, but they count towards the
// discount tier and the discount is taken on the whole basket — the backend's
// `existing_selections`. Leaving them out quoted a new category at the lone-seat
// tier while the link charged the family tier (₹5,625 quoted, ₹4,843.75 billed).
export const computeEnrolmentFee = (
  selections = [],
  config = PRICING_CONFIG,
  startDate = null,
  kitTotal = 0,
  heldSelections = []
) => {
  const programs = config?.programs || PRICING_CONFIG.programs
  const discountTiers = config?.discountTiers || PRICING_CONFIG.discountTiers
  const rate = (sel) => {
    const row = programs.find((p) => p.program === sel.program)
    if (!row) return 0
    return sel.frequency === '2x' ? row.twiceWeekly : row.onceWeekly
  }
  const seats = (sels) =>
    sels.reduce((n, sel) => n + (sel.frequency === '2x' ? 2 : 1), 0)
  const base = selections.reduce((sum, sel) => sum + rate(sel), 0)
  const basketBase = heldSelections.reduce((sum, sel) => sum + rate(sel), base)
  const heldCount = seats(heldSelections)
  const count = seats(selections) + heldCount
  const tier = [...discountTiers]
    .sort((a, b) => b.minEnrolments - a.minEnrolments)
    .find((t) => count >= t.minEnrolments)
  const discountPercent = tier ? tier.discountPercent : 0
  // Never more than this bill charges — see compute_enrolment_fee.
  const discount = Math.min(
    Math.round((basketBase * discountPercent) / 100),
    base
  )
  const monthlyNet = base - discount

  // Prorate the discounted monthly figure, so the family discount is not lost
  // on a half month.
  const { current, next } = prorataFactors(startDate)
  const monthsBilled = current + next
  const kit = kitTotal || 0
  const total = round2(monthlyNet * monthsBilled + kit)
  const gross = round2(base * monthsBilled + kit)

  return {
    base,
    discountPercent,
    // Whole-bill figures, so gross - discount === total for any month count.
    // The kit sits inside gross so the identity survives it too.
    discount: round2(gross - total),
    total,
    monthlyNet,
    currentMonthFactor: current,
    nextMonthFactor: next,
    monthsBilled,
    kitTotal: kit,
    heldCount
  }
}
